/**
 * Bridge from wizard answers to the authoritative refinance form state
 * (WI-TV-W5-002, extended by WI-TV-W5-003).
 *
 * The wizard stores answers as flat strings keyed by the `ReferralFormState`
 * field names, so building form state is a direct projection with no renaming
 * and no new payload semantics: `buildPayload` and `validateRefinance` keep
 * owning shaping and validation exactly as they do for the existing form.
 *
 * The one place this projection is more than a copy is the application-level
 * military answer, which is tri-state on the wire (asserted true, asserted
 * false, or absent) while the form state models the assertion and its value
 * separately. Both are derived independently from the current answers, and an
 * unanswered or "Not sure" application asserts neither.
 *
 * The optional co-borrower tail (WI-TV-W7-002) projects through the same shared
 * helper the purchase journey uses, so the two journeys emit the same optional
 * `coBorrower` for the same answers.
 */
import { emptyFormState, type ReferralFormState } from "../models/referral";
import {
  CREDIT_SCORE_RANGE_KEY,
  CURRENT_LOAN_BALANCE_KEY,
  EMAIL_KEY,
  ESTIMATED_VALUE_KEY,
  FIRST_NAME_KEY,
  LAST_NAME_KEY,
  LOAN_AMOUNT_KEY,
  MILITARY_ANSWER_KEY,
  MILITARY_COMPONENT_KEY,
  PHONE_KEY,
  PROPERTY_CITY_KEY,
  PROPERTY_STATE_KEY,
  PROPERTY_STREET_KEY,
  PROPERTY_TYPE_KEY,
  PROPERTY_ZIP_KEY,
  REFINANCE_TYPE_KEY,
  isCashOutRefinance,
  phoneDigits,
  toWireRefinanceType,
} from "./refinance-steps";
import { applyCoBorrowerFormState } from "./coborrower-steps";

/** Projects a wizard value snapshot onto a refinance {@link ReferralFormState}. */
export function toRefinanceFormState(values: Readonly<Record<string, string>>): ReferralFormState {
  const state = emptyFormState();
  const read = (key: string): string => values[key] ?? "";

  state.loanPurpose = "refinance";
  // The VA Streamline goal is UI-only; it routes through the existing rate-and-term
  // wire flow until the backend supports IRRRL as a distinct loan purpose.
  state.refinanceType = toWireRefinanceType(read(REFINANCE_TYPE_KEY));
  state.propertyStreetAddress = read(PROPERTY_STREET_KEY);
  state.propertyCity = read(PROPERTY_CITY_KEY);
  state.propertyState = read(PROPERTY_STATE_KEY);
  state.propertyZipCode = read(PROPERTY_ZIP_KEY);
  state.propertyType = read(PROPERTY_TYPE_KEY);
  state.estimatedValue = read(ESTIMATED_VALUE_KEY);
  state.currentLoanBalance = read(CURRENT_LOAN_BALANCE_KEY);
  // Only cash-out states a separate requested amount. Rate-and-term borrows
  // exactly the balance it still owes, and the payload derives that amount from
  // the current balance, so a value left over from an earlier cash-out answer
  // is never projected here (WI-TV-W65-006).
  state.loanAmount = isCashOutRefinance(values) ? read(LOAN_AMOUNT_KEY) : "";

  state.borrower.firstName = read(FIRST_NAME_KEY).trim();
  state.borrower.lastName = read(LAST_NAME_KEY).trim();
  state.borrower.email = read(EMAIL_KEY).trim();
  // The payload always carries the ten digits the borrower intended, whatever
  // formatting characters they typed around them.
  state.borrower.phone = phoneDigits(read(PHONE_KEY));
  state.borrower.creditScoreRange = read(CREDIT_SCORE_RANGE_KEY);

  const answer = read(MILITARY_ANSWER_KEY);
  state.militaryAnswered = answer === "yes" || answer === "no";
  state.isMilitary = answer === "yes";
  state.militaryBranch = answer === "yes" ? read(MILITARY_COMPONENT_KEY) : "";
  // Still-looking is a purchase-only behavior and never applies to refinance.
  state.propertyStillLooking = false;

  applyCoBorrowerFormState(state, values);
  return state;
}
